import {
  activeWorkspaceIdAtom,
  selectedProcessIdAtom,
} from '../atoms/workspace';
import { AuthenticatedLayout } from '../components/Layout/AuthenticatedLayout';
import { ProcessDetailPanel } from '../components/ProcessDetailPanel/ProcessDetailPanel';
import { ProcessListPanel } from '../components/ProcessListPanel/ProcessListPanel';
import { TimelineDrawer } from '../components/Timeline/TimelineDrawer';
import { TimelineStrip } from '../components/Timeline/TimelineStrip';
import { WorkspaceList } from '../components/WorkspaceList/WorkspaceList';
import { routes } from '../routes';
import { Button } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useAtom, useAtomValue } from 'jotai';
import { Archive } from 'lucide-react';
import React, { useState } from 'react';
import { Link } from 'wouter';

type MobileView = 'detail' | 'processes' | 'workspaces';

export const Dashboard: React.FC = () => {
  const activeWorkspaceId = useAtomValue(activeWorkspaceIdAtom);
  const [selectedProcessId, setSelectedProcessId] = useAtom(
    selectedProcessIdAtom,
  );
  const [mobileView, setMobileView] = useState<MobileView>('processes');
  // Process actions (snooze, archive, move, delete) clear selectedProcessId
  // on success. On mobile that would leave us stranded on a "Select a process"
  // placeholder, so derive the active mobile view: when detail loses its
  // target, render the process list instead. Deriving (rather than syncing
  // via useEffect) avoids the cascading render warning.
  const activeMobileView: MobileView =
    mobileView === 'detail' && selectedProcessId === null
      ? 'processes'
      : mobileView;
  // CSR-only app, so seed useMediaQuery with the actual current match to
  // avoid a wrong-layout first paint that flashes on mobile and races tests.
  const isDesktop = useMediaQuery(
    '(min-width: 768px)',
    typeof window === 'undefined'
      ? true
      : window.matchMedia('(min-width: 768px)').matches,
  );

  return (
    <AuthenticatedLayout
      headerRight={
        <Button
          color="gray"
          component={Link}
          href={routes.archive}
          leftSection={<Archive size={14} />}
          size="compact-sm"
          variant="subtle"
        >
          Archive
        </Button>
      }
    >
      {isDesktop ? (
        <div className="h-full flex bg-[#e8e4df] dark:bg-[#1a1d21]">
          <div className="flex-1 relative ml-[180px] min-w-[200px] bg-[#f0ece7] dark:bg-[#22262b] rounded-tl-xl border-t border-l border-black/15 dark:border-white/15 shadow-[inset_-1px_0_0_rgba(0,0,0,0.15)] dark:shadow-[inset_-1px_0_0_rgba(255,255,255,0.15)]">
            <div className="w-[180px] shrink-0 -ml-[180px] absolute">
              <WorkspaceList />
            </div>
            <ProcessListPanel />
          </div>
          <div className="w-1/2 shrink-0 bg-[#faf8f5] dark:bg-[#2a2f36] border-t border-black/15 dark:border-white/15">
            <ProcessDetailPanel />
          </div>
          <TimelineStrip workspaceId={activeWorkspaceId} />
        </div>
      ) : (
        <div className="h-full">
          {activeMobileView === 'detail' ? (
            <div className="h-full bg-[#faf8f5] dark:bg-[#2a2f36]">
              <ProcessDetailPanel
                onBack={() => {
                  setSelectedProcessId(null);
                  setMobileView('processes');
                }}
              />
            </div>
          ) : activeMobileView === 'processes' ? (
            <div className="h-full bg-[#f0ece7] dark:bg-[#22262b]">
              <ProcessListPanel
                onBackToWorkspaces={() => setMobileView('workspaces')}
                onProcessSelect={() => setMobileView('detail')}
                showTimelineButton
              />
            </div>
          ) : (
            <div className="h-full bg-[#e8e4df] dark:bg-[#1a1d21]">
              <WorkspaceList
                onWorkspaceSelected={() => setMobileView('processes')}
              />
            </div>
          )}
        </div>
      )}

      <TimelineDrawer workspaceId={activeWorkspaceId} />
    </AuthenticatedLayout>
  );
};
